import { Dialog, Stack, TextField } from '@fluentui/react'
import { CopyRegular } from '@fluentui/react-icons'
import { useContext, useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import AccessRestrictedModal from '../../components/dialogs/AccessRestrictedModal/AccessRestrictedModal'
import { AppStateContext } from '../../state/AppProvider'

import { Alert, Button, ClickAwayListener, ClickAwayListenerProps, Snackbar, Typography } from '@mui/material'
import { CosmosDBStatus } from '../../api'
import { ChatHistoryPanel } from '../../components/ChatHistory/ChatHistoryPanel'
import CustomDialog from '../../components/dialogs/CustomDialog/CustomDialog'
import styles from './Layout.module.css'

const Layout = () => {
  const [isSharePanelOpen, setIsSharePanelOpen] = useState<boolean>(false)
  const [copyClicked, setCopyClicked] = useState<boolean>(false)
  const [copyText, setCopyText] = useState<string>('Copy URL')
  const appStateContext = useContext(AppStateContext)
  const location = useLocation()
  const navigate = useNavigate()

  const handleSharePanelDismiss = () => {
    setIsSharePanelOpen(false)
    setCopyClicked(false)
    setCopyText('Copy URL')
  }

  const handleCopyClick = () => {
    navigator.clipboard.writeText(window.location.href)
    setCopyClicked(true)
  }

  const handleNewChat = () => {
    if (location.pathname !== '/app') {
      navigate('/app')
    }
    appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: null })
    appStateContext?.dispatch({ type: 'SET_IS_NEW_CHAT_DIALOG_OPEN', payload: false })
  }

  const handleCloseNewChatDialog = () => {
    appStateContext?.dispatch({ type: 'SET_IS_NEW_CHAT_DIALOG_OPEN', payload: false })
  }

  useEffect(() => {
    if (copyClicked) {
      setCopyText('Copied URL')
    }
  }, [copyClicked])

  useEffect(() => {}, [appStateContext?.state.isCosmosDBAvailable.status])

  useEffect(() => {
    appStateContext?.dispatch({ type: 'SET_SHOULD_VALIDATE_FORM', payload: false })
  }, [appStateContext?.state.currentChat?.extended_data?.experiment_form_step])

  return (
    <div className={styles.layout}>
      <AccessRestrictedModal open={appStateContext?.state.isUserActive === false} />
      {appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured && <ChatHistoryPanel />}
      <Outlet />
      <Dialog
        onDismiss={handleSharePanelDismiss}
        hidden={!isSharePanelOpen}
        styles={{
          main: [
            {
              selectors: {
                ['@media (min-width: 480px)']: {
                  maxWidth: '600px',
                  background: '#FFFFFF',
                  boxShadow: '0px 14px 28.8px rgba(0, 0, 0, 0.24), 0px 0px 8px rgba(0, 0, 0, 0.2)',
                  borderRadius: '8px',
                  maxHeight: '200px',
                  minHeight: '100px',
                },
              },
            },
          ],
        }}
        dialogContentProps={{
          title: 'Share the web app',
          showCloseButton: true,
        }}>
        <Stack
          horizontal
          verticalAlign="center"
          style={{ gap: '8px' }}>
          <TextField
            className={styles.urlTextBox}
            defaultValue={window.location.href}
            readOnly
          />
          <div
            className={styles.copyButtonContainer}
            role="button"
            tabIndex={0}
            aria-label="Copy"
            onClick={handleCopyClick}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? handleCopyClick() : null)}>
            <CopyRegular className={styles.copyButton} />
            <span className={styles.copyButtonText}>{copyText}</span>
          </div>
        </Stack>
      </Dialog>
      <Snackbar
        open={appStateContext?.state.snackbarState !== null}
        autoHideDuration={appStateContext?.state.snackbarState?.severity === 'error' ? null : 3000}
        onClose={() => appStateContext?.dispatch({ type: 'SET_SNACKBAR_STATE', payload: null })}
        slots={{
          clickAwayListener: (props: ClickAwayListenerProps & { ownerState?: unknown }) => {
            const { ownerState, ...rest } = props
            return <ClickAwayListener {...rest} />
          },
        }}>
        <Alert
          onClose={() => appStateContext?.dispatch({ type: 'SET_SNACKBAR_STATE', payload: null })}
          severity={appStateContext?.state.snackbarState?.severity}
          classes={{
            colorSuccess: styles.snackbarSuccess,
            colorWarning: styles.snackbarWarning,
          }}
          action={
            appStateContext?.state.snackbarState?.action ? (
              <Button
                color="inherit"
                size="small"
                onClick={appStateContext.state.snackbarState.action.onClick}>
                {appStateContext.state.snackbarState.action.label}
              </Button>
            ) : undefined
          }>
          {appStateContext?.state.snackbarState?.message}
        </Alert>
      </Snackbar>
      <CustomDialog
        title="New Chat"
        open={appStateContext?.state.isNewChatDialogOpen ?? false}
        setClose={handleCloseNewChatDialog}
        primaryAction={handleNewChat}
        secondaryAction={handleCloseNewChatDialog}
        primaryActionButtonText="Start a new chat"
        secondaryActionButtonText="Cancel">
        <Typography className={styles.newChatDialogContentText}>
          Are you sure you want to start a new conversation? Don't worry, your progress will be saved and can be
          accessed through the <strong>Chat History</strong> at any time.
        </Typography>
      </CustomDialog>
    </div>
  )
}

export default Layout
